import type { InvoiceSnapshot } from '../../shared/types'
import { formatCents } from '../../shared/money'
import { formatHours2 } from '../../shared/duration'

// ISO 'YYYY-MM-DD' -> 'MM/DD/YYYY'
function mdy(iso?: string | null): string {
  if (!iso) return ''
  const [y, m, d] = iso.split('-')
  return y && m && d ? `${m}/${d}/${y}` : iso
}

// Renders a frozen invoice snapshot. Used both in the generation preview and
// the saved invoice view, and is the print target (.invoice-sheet). Reads ONLY
// from the snapshot — never from live client/category data.
export default function InvoiceDocument({ snap }: { snap: InvoiceSnapshot }) {
  const showTax = snap.tax_rate_bp > 0 || snap.tax_cents > 0
  return (
    <div
      className="invoice-sheet mx-auto max-w-[7in] bg-white p-6 text-[15px] leading-relaxed text-slate-900 shadow-sm sm:p-10"
      style={{ fontFamily: 'var(--font-invoice)' }}
    >
      {/* Header: sender + INVOICE meta */}
      <div className="flex items-start justify-between border-b border-slate-300 pb-6">
        <div>
          {snap.sender.company && <div className="text-xl font-semibold">{snap.sender.company}</div>}
          {snap.sender.name && <div>{snap.sender.name}</div>}
          {snap.sender.address && <div className="whitespace-pre-line text-slate-600">{snap.sender.address}</div>}
          <div className="mt-1 text-slate-600">
            {snap.sender.email && <div>{snap.sender.email}</div>}
            {snap.sender.phone && <div className="tabular">{snap.sender.phone}</div>}
            {snap.sender.website && <div>{snap.sender.website}</div>}
            {snap.sender.tax_id && <div className="tabular">{snap.sender.tax_id}</div>}
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-semibold tracking-wide text-slate-800">
            INVOICE <span className="tabular font-normal text-slate-600">{snap.invoice_number}</span>
          </div>
          <table className="tabular mt-3 ml-auto text-sm text-slate-600">
            <tbody>
              <tr>
                <td className="pr-3 text-left text-slate-400">Period</td>
                <td className="text-left">
                  {mdy(snap.period_start)} – {mdy(snap.period_end)}
                </td>
              </tr>
              <tr>
                <td className="pr-3 text-left text-slate-400">Issued</td>
                <td className="text-left">{mdy(snap.issued_date)}</td>
              </tr>
              {snap.due_date && (
                <tr>
                  <td className="pr-3 text-left text-slate-400">Due</td>
                  <td className="text-left">{mdy(snap.due_date)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bill to */}
      <div className="mt-6">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Bill to</div>
        <div className="mt-1 font-semibold">{snap.client.legal_name}</div>
        {snap.client.address && <div className="whitespace-pre-line text-slate-600">{snap.client.address}</div>}
        {snap.client.contact_email && <div className="text-slate-600">{snap.client.contact_email}</div>}
      </div>

      {/* Line items */}
      <table className="mt-6 w-full border-collapse">
        <thead>
          <tr className="border-b-2 border-slate-300 text-left text-xs uppercase tracking-wide text-slate-500">
            <th className="py-2">{snap.line_mode === 'entry' ? 'Date / description' : 'Category'}</th>
            <th className="py-2 text-right">Hours</th>
            <th className="py-2 text-right">Rate</th>
            <th className="py-2 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {snap.lines.map((l, i) => (
            <tr key={i} className="border-b border-slate-200 align-top">
              <td className="py-2 pr-4">
                {snap.line_mode === 'entry' && l.detail ? (
                  <div>
                    <span className="tabular text-slate-500">{mdy(l.detail)}</span> — {l.label}
                  </div>
                ) : (
                  l.label
                )}
              </td>
              <td className="py-2 text-right tabular">{formatHours2(l.minutes)}</td>
              <td className="py-2 text-right tabular text-slate-600">{formatCents(l.rate_cents)}</td>
              <td className="py-2 text-right tabular">{formatCents(l.amount_cents)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totals */}
      <div className="mt-4 flex justify-end">
        <table className="tabular w-64">
          <tbody>
            <tr>
              <td className="py-1 text-right text-slate-500">Subtotal</td>
              <td className="py-1 text-right">{formatCents(snap.subtotal_cents)}</td>
            </tr>
            {showTax && (
              <tr>
                <td className="py-1 text-right text-slate-500">
                  Tax ({(snap.tax_rate_bp / 100).toFixed(2)}%)
                </td>
                <td className="py-1 text-right">{formatCents(snap.tax_cents)}</td>
              </tr>
            )}
            <tr className="border-t border-slate-300 text-lg font-semibold">
              <td className="py-2 text-right">Total</td>
              <td className="py-2 text-right">{formatCents(snap.total_cents)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {snap.sender.payment_instructions && (
        <div className="mt-10 border-t border-slate-200 pt-4 text-sm text-slate-600">
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Payment</div>
          <div className="whitespace-pre-line">{snap.sender.payment_instructions}</div>
        </div>
      )}

      {snap.itemized && snap.itemized.length > 0 && (
        <div className="invoice-page-break mt-12 pt-6">
          <div className="mb-1 text-2xl font-semibold tracking-wide text-slate-800">ITEMIZED DETAIL</div>
          <div className="tabular mb-4 text-slate-600">
            {snap.invoice_number} · {mdy(snap.period_start)} – {mdy(snap.period_end)}
          </div>
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b-2 border-slate-300 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="py-2" style={{ width: '6.5rem' }}>Date</th>
                <th className="py-2">Description</th>
                <th className="py-2">Category</th>
                <th className="py-2 text-right">Hours</th>
              </tr>
            </thead>
            <tbody>
              {snap.itemized.map((l, i) => (
                <tr key={i} className="border-b border-slate-200 align-top">
                  <td className="py-1.5 pr-2 tabular text-slate-500">{mdy(l.detail)}</td>
                  <td className="py-1.5 pr-3">{l.label}</td>
                  <td className="py-1.5 pr-3 text-slate-600">{l.category}</td>
                  <td className="py-1.5 text-right tabular">{formatHours2(l.minutes)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-300 font-semibold">
                <td className="py-2" colSpan={3}>
                  Total
                </td>
                <td className="py-2 text-right tabular">
                  {formatHours2(snap.itemized.reduce((s, l) => s + l.minutes, 0))}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  )
}
